import { employmentTypeOptions } from "@/components/company-settings/config/employmentTypeOptions";
import cleanDeep from "clean-deep";
import { format, parse } from "date-fns";

/**
 * Takes a string and removes any special characters, to convert to an Integer
 * ! Does not work with negatives
 * @param {*} value Ex. 1234.fas4.431d
 * @returns Formatted to Integer Ex. 12344431
 */
const validateInteger = (value) => {
	value = String(value).replace(/[^\d]/g, "");
	if (!value) return value;
	return parseInt(value);
};

/**
 * Takes a string and removes any special characters and limits to only one decimal point, to convert to a Float
 * ! Does not work with negatives
 * @param {*} value Ex. 1234.fas4.431d
 * @returns Formatted to Float Ex. 1234.4431
 */
const validateFloat = (value) => {
	if (!value) return value;
	const splitValues = value.replace(/[^\d\.]/g, "").split(".");
	const firstValue = splitValues.shift();
	const lastValues = splitValues.reduce((curr, next) => curr + next, "");

	return firstValue + (value.includes(".") ? "." : "") + lastValues;
};

/**
 * Employment Type Name Params
 * @typedef {Object} EmploymentTypeNameParams
 * @property {Number | String} employmentType Employment Type Value
 * @property {Boolean} short Shorten the Employment Type Name
 */

/**
 * Returns the Employment Type Name based on the value
 * @param {Number | String | EmploymentTypeNameParams} args Ex. 101
 * @returns Employment Type Name Ex. 'Full Time'
 * @param {EmploymentTypeNameParams} args Ex. { employmentType: 101, short: true }
 * @returns Employment Type Name Ex. 'FT'
 * @param {EmploymentTypeNameParams} args Ex. { employmentType: 101 }
 * @returns Employment Type Name Ex. 'Full Time'
 */
const getEmploymentTypeName = (args) => {
	let employmentType;
	let short;
	if (typeof args === "object") {
		short = args.short;
		employmentType = args.employmentType;
	}

	const other = employmentTypeOptions.find((item) => item.value == 503);
	const selected = employmentTypeOptions.find((item) => item.value == employmentType);

	return (selected || other)?.[short ? "short" : "label"];
};

const getPayFrequencyName = (payPeriodFrequency) => {
	if (typeof payPeriodFrequency === "object") payPeriodFrequency = payPeriodFrequency.payFrequency;

	switch (String(payPeriodFrequency)) {
		case "105":
			return "Hour";
		case "104":
			return "Day";
		case "103":
			return "Week";
		case "107":
			return "Fortnight (Two Weeks)";
		case "106":
			return "Four Weeks";
		case "102":
			return "Month";
		case "101":
			return "Year";
	}
};

/**
 * Takes a string as an input and capitalises every first character of each word seperated by a space
 * @param {*} string Ex. 'KEELAN VELLA'
 * @returns Formatted string Ex. 'Keelan Vella'
 */
const toTitleCase = (string) => {
	if (!string) return "";

	let stringToProcess = String(string);

	const charactersToMatch = ["-", "/", "."];
	//Find All Positions of Characters that Match these Criteria
	const characterIndex = [...stringToProcess].reduce((prev, curr, index) => {
		const matchedCharacter = charactersToMatch.find((item) => item === curr);
		if (matchedCharacter) prev.push({ character: matchedCharacter, index });
		return prev;
	}, []);

	//Clear Characters from string
	charactersToMatch.map((character) => {
		stringToProcess = stringToProcess.replaceAll(character, " ");
	});

	// Capitalise
	const capitalized = stringToProcess
		?.split(" ")
		?.map((c) => c?.[0]?.toUpperCase() + c?.substring(1)?.toLowerCase())
		?.join(" ");
	// Convert To Array
	const stringArray = [...capitalized];

	//Reformat string with previously removed characters
	characterIndex.map((item) => {
		const { character, index } = item;
		stringArray.splice(index, 1, character);
	});

	const finishedString = stringArray.join("");
	return finishedString;
};

const trimAll = (string) => {
	if (!string) return "";
	return String(string).replace(/\s/g, "");
};

const getCurrency = (jurisdiction) => {
	switch (jurisdiction) {
		case "MT":
		case "IE":
			return "EUR";
		case "UK":
			return "GBP";
		default:
			return "EUR";
	}
};

/**
 * Constructs a function that Converts a String in Date format to a Date Object
 * Defaults to yyyy-MM-dd
 * @param {String} dateString Input in String format
 * @param {*} ifNullReturn If input is undefined, return [undefined, null, new Date()]
 * @param {String} format Date FNS format for dates, to use instead of default
 * @returns {Function} returns a function to convert a string into a date object
 */
const stringToDateConstructor = ({ ifNullReturn, format }) => {
	return (dateString) => {
		switch (ifNullReturn) {
			case "undefined":
				return undefined;
			case "null":
				return null;
			case "today":
			default: {
				if (!dateString) return new Date();
			}
		}
		return parse(dateString, format ? format : "yyyy-MM-dd", new Date());
	};
};

/**
 * Converts a String Date Format into a Date Object, defaults to today's Date if date is undefined
 * @param {String} dateString Input in String format
 * @returns {Date} Returns a Date Object
 */
const stringToDate = (dateString) => {
	const stringToDate = stringToDateConstructor({ ifNullReturn: "today", format: "yyyy-MM-dd" });
	return stringToDate(dateString);
};

/**
 * Converts a Date Object into a Date in String format yyyy-MM-dd
 */
const dateToString = (date) => {
	if (!date) return undefined;
	return format(date, "yyyy-MM-dd");
};

/**
 * A function that accepts the minimum number of decimal places and the maximum number of decimal places to format numbers with said restrictions.
 *
 * Ex. toDecimal(0,2) returns a function which takes a value and formats to a minimum of 0 decimal places and a maximum of two
 *
 * Ex. const roundTo2Min0 = toDecimal(0,2)
 *
 * roundTo2Min0(1200.00) => 1,200
 *
 * roundTo2Min2(1200.011) => 1,200.01
 * @param {*} min Ex. 0
 * @param {*} max Ex. 2
 * @returns A function which takes a parameter and converts the number to the specified min and max decimal places
 */
const toDecimal = (min, max, stripIfInteger) => {
	return (value) =>
		(!isNaN(parseFloat(value)) ? parseFloat(value) : 0).toLocaleString(undefined, {
			minimumFractionDigits: min,
			maximumFractionDigits: max,
			trailingZeroDisplay: stripIfInteger ? "stripIfInteger" : "auto",
		});
};

/**
 * Returns a function that converts any number value to to decimal places and formats with commas dividing thousands. ex: 100000.1 => 100,000.10
 * @param {Integer} value Number ex. 100000.1
 * @returns Formatted Number ex. 100,000.10
 */
const roundTo2 = (value) => {
	const round2 = toDecimal(2, 2);
	return round2(value);
};

/**
 * Extension of URLSearchParams which returns query parameters including a '?' and removes any undefined values/parameters from the object
 * @param {{}} object Object to construct query parameters with (Ex. { name: 'Keelan', surname: 'Vella', age: undefined })
 * @returns {String} Query String Parameters (Ex. '?name=Keelan&surname=Vella')
 */
const constructQueryParams = (object) => {
	const objectToUse = cleanDeep({ ...object });

	if (Object.keys(objectToUse).length == 0) return "";

	return `?${new URLSearchParams(objectToUse).toString()}`;
};

/**
 * Function to return a number with its ordinal
 * @param {Number} n The number to return with its ordinal
 * @returns {String} The number with its ordinal
 */
const getNumberWithOrdinal = (n) => {
	if (typeof n === "undefined") return;
	var s = ["th", "st", "nd", "rd"],
		v = n % 100;
	return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const getTaxYear = (date, jurisdiction) => {
	if (!date) return {};
	let year = parseFloat(date.substring(0, 4));
	switch (jurisdiction) {
		case "UK": {
			const possibleTaxYearStart = `${year}-04-06`;
			if (possibleTaxYearStart > date) year = year - 1;

			const taxYearStart = `${year}-04-06`;
			const taxYearEnd = `${year + 1}-04-05`;
			const taxPeriod = `${format(stringToDate(taxYearStart), "do MMMM yyyy")} - ${format(
				stringToDate(taxYearEnd),
				"do MMMM yyyy",
			)}`;

			const periods =
				year == "2022"
					? [
							{ version: "v1", start: `${year}-04-06`, end: `${year}-07-05` },
							{ version: "v2", start: `${year}-07-06`, end: `${year}-11-05` },
							{ version: "v3", start: `${year}-11-06`, end: `${year + 1}-04-05` },
					  ]
							.filter((period) => period.end >= date)
							.map((period) => ({
								...period,
								taxPeriod: `${format(stringToDate(period.start), "do MMMM yyyy")} - ${format(
									stringToDate(period.end),
									"do MMMM yyyy",
								)}`,
							}))
					: undefined;

			return {
				taxPeriod,
				taxYearStart,
				taxYearEnd,
				periods,
				year,
			};
		}
		case "MT":
			return { taxPeriod: format(stringToDate(date), "do MMMM yyyy"), year };
	}
};

const validateMalteseID = (value) => {
	return /^\d+[mMlLgGhHpPAa]$/.test(value);
};

export {
	stringToDateConstructor,
	stringToDate,
	dateToString,
	validateInteger,
	validateFloat,
	getEmploymentTypeName,
	getPayFrequencyName,
	toTitleCase,
	getCurrency,
	toDecimal,
	roundTo2,
	constructQueryParams,
	trimAll,
	getTaxYear,
	validateMalteseID,
	getNumberWithOrdinal,
};
